import { useCallback, useEffect, useRef } from "react";
import { DbtDocsShareDetails } from "../collaboration/types";
import classes from "./styles.module.scss";
import dbtDocsAngularApp from "../resources/dbtDocsAngularApp.js";
import {
  getCodeBlockElementInPage,
  getSelectionAllowedElements,
} from "../collaboration/highlightUtils.js";
import {
  useDbtDocsCollaborationDispatch,
  useDbtDocsCollaborationSelector,
} from "../collaboration/DbtDocsCollaborationProvider.js";
import {
  setDocsAppRendered,
  updateCodeblockLoaded,
  updateCurrentPage,
} from "../collaboration/DbtDocsReducer.js";
import { getResourceTypeAndPageFromUrlHash } from "./dbtDocsUtils.js";
import { bindSelectionEndEvent } from "../collaboration/selectionEndEvent.js";

interface HttpProvider {
  interceptors: {
    push: (interceptor: unknown) => void;
  };
}

interface Props {
  shareDetails: DbtDocsShareDetails;
  onSelectionEnd: (e: CustomEvent) => void;
}

const DbtDocsRenderer = ({ shareDetails, onSelectionEnd }: Props) => {
  const angularRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDbtDocsCollaborationDispatch();
  const docsAppRendered = useDbtDocsCollaborationSelector(
    state => state.docsAppRendered
  ) as boolean;

  const onLocationChange = useCallback(() => {
    dispatch(updateCurrentPage(getResourceTypeAndPageFromUrlHash()));
    dispatch(updateCodeblockLoaded(false));

    setTimeout(() => {
      getSelectionAllowedElements().forEach(element => {
        if (element) {
          bindSelectionEndEvent(element);
          element.removeEventListener("selectionend", onSelectionEnd);
          element.addEventListener("selectionend", onSelectionEnd);
        }
      });

      const codeSection = getCodeBlockElementInPage();
      if (codeSection) {
        // dbt docs page code block is not aligned properly with line numbers
        // this will mess up the selection range calculation
        // to handle that issue, inserting an empty div element with line break
        // in css, line height is set to make sure the lines are aligned properly
        codeSection.innerHTML = codeSection.innerHTML.replace(/\n\n/g, "\n<div>\n</div>");
        dispatch(updateCodeblockLoaded(true));
      }
    }, 100);
  }, [dispatch, onSelectionEnd]);

  useEffect(() => {
    // Hack to load the angular js file
    // Without this, the js file is not bundled
    console.log("dbtDocsAngularApp rendered", !!dbtDocsAngularApp);

    // @ts-expect-error valid
    const angular = window.angular;
    if (angular && angular.module) {
      if (angular.element(angularRef.current).scope()) {
        console.log("dbt docs already initialized");
        return;
      }
      const app = angular.module("dbt");
      app.config([
        "$httpProvider",
        function ($httpProvider: HttpProvider) {
          const interceptor = [
            "$q",
            "$rootScope",
            function () {
              const service = {
                request: function (config: { url: string }) {
                  if (config.url.includes("manifest.json") && shareDetails.manifest_presigned_url) {
                    config.url = shareDetails.manifest_presigned_url;
                  }
                  if (config.url.includes("catalog.json") && shareDetails.catalog_presigned_url) {
                    config.url = shareDetails.catalog_presigned_url;
                  }
                  return config;
                },
              };

              return service;
            },
          ];

          $httpProvider.interceptors.push(interceptor);
        },
      ]);
      angular.bootstrap(angularRef.current, ["dbt"]);

      window.addEventListener("hashchange", onLocationChange);

      if (angularRef.current) {
        const callback = () => {
          if (!docsAppRendered) {
            const panelBody = angularRef.current?.querySelector(".panel-body") as HTMLElement;
            if (panelBody.innerText !== "(loading)") {
              dispatch(setDocsAppRendered(true));
              observer.disconnect();
            }
          }
        };

        const config = { attributes: false, childList: true, subtree: true };
        const observer = new MutationObserver(callback);
        observer.observe(angularRef.current, config);
      }
    }
  }, [
    shareDetails.manifest_presigned_url,
    shareDetails.catalog_presigned_url,
    onSelectionEnd,
    dispatch,
    docsAppRendered,
    onLocationChange,
  ]);

  return (
    <>
      {/* icons for dbt docs render */}
      <div style={{ display: "none" }}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
          <title>icons</title>
          <g id="icn">
            <rect width="16" height="16" fill="none" />
          </g>
          <g id="icn-menu">
            <path
              d="M14.5,8a.5.5,0,0,1-.5.5H2a.5.5,0,0,1,0-1H14A.5.5,0,0,1,14.5,8ZM2,3H14a.5.5,0,0,0,0-1H2A.5.5,0,0,0,2,3ZM14,13H2a.5.5,0,0,0,0,1H14a.5.5,0,0,0,0-1Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-dbt">
            <path
              d="M12.6,9.265l0,0L10.9,6.325a3.31,3.31,0,0,0-.529-.7h0a3.264,3.264,0,0,0-3.184-.867,4.022,4.022,0,0,1,4.194.938,3.977,3.977,0,0,1,.635.833l.38.657a2.488,2.488,0,0,1,.2-.465l2.226-3.851a1.2,1.2,0,0,0-.2-1.5,1.2,1.2,0,0,0-1.5-.2h0L9.27,3.4a2.539,2.539,0,0,1-2.54,0L2.877,1.178A1.253,1.253,0,0,0,2.247,1a1.247,1.247,0,0,0-.871.375,1.2,1.2,0,0,0-.2,1.5L3.4,6.726a2.542,2.542,0,0,1,0,2.539l-2.226,3.85a1.2,1.2,0,0,0,.2,1.5,1.2,1.2,0,0,0,1.5.2L6.73,12.588a2.455,2.455,0,0,1,.465-.2l-.657-.379a3.857,3.857,0,0,1-1.462-1.467,4.118,4.118,0,0,1-.288-3.368,3.453,3.453,0,0,0,.33,2.507A3.141,3.141,0,0,0,6.329,10.89l2.94,1.7h0l3.853,2.224a1.243,1.243,0,0,0,1.7-1.7Zm.814-7.353a.478.478,0,1,1,0,.676A.48.48,0,0,1,13.41,1.912ZM2.59,2.588a.478.478,0,1,1,0-.676A.479.479,0,0,1,2.59,2.588Zm0,11.49a.478.478,0,1,1,0-.676A.479.479,0,0,1,2.59,14.078Zm6.827-5.9a1.022,1.022,0,1,0-1.231,1.23,1.433,1.433,0,1,1,1.231-1.23Zm4.67,5.9a.478.478,0,1,1,0-.676A.479.479,0,0,1,14.087,14.078Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-doc-on">
            <path
              d="M13.707,4.707,10.293,1.293A1,1,0,0,0,9.586,1H3A1,1,0,0,0,2,2V14a1,1,0,0,0,1,1H13a1,1,0,0,0,1-1V5.414A1,1,0,0,0,13.707,4.707ZM5,4H9V5H5Zm6,8H5V11h6Zm0-2H5V9h6Zm0-2H5V7h6Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-doc">
            <path
              d="M9.586,2h0L13,5.414V14H3V2H9.586m0-1H3A1,1,0,0,0,2,2V14a1,1,0,0,0,1,1H13a1,1,0,0,0,1-1V5.414a1,1,0,0,0-.293-.707L10.293,1.293A1,1,0,0,0,9.586,1Z"
              transform="translate(0 0)"
            />
            <rect x="5" y="11" width="6" height="1" fill="#fff" />
            <polygon points="11 11 5 11 5 12 11 12 11 11 11 11" />
            <rect x="5" y="9" width="6" height="1" fill="#fff" />
            <polygon points="11 9 5 9 5 10 11 10 11 9 11 9" />
            <rect x="5" y="7" width="6" height="1" fill="#fff" />
            <polygon points="11 7 5 7 5 8 11 8 11 7 11 7" />
            <rect x="5" y="4" width="4" height="1" fill="#fff" />
            <polygon points="9 4 5 4 5 5 9 5 9 4 9 4" />
          </g>
          <g id="icn-down">
            <path
              d="M8,10.5a.5.5,0,0,1-.354-.146l-3-3a.5.5,0,0,1,.708-.708L8,9.293l2.646-2.647a.5.5,0,0,1,.708.708l-3,3A.5.5,0,0,1,8,10.5Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-up">
            <path
              d="M11,9.5a.5.5,0,0,1-.354-.146L8,6.707,5.354,9.354a.5.5,0,0,1-.708-.708l3-3a.5.5,0,0,1,.708,0l3,3A.5.5,0,0,1,11,9.5Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-left">
            <path
              d="M9.5,5a.5.5,0,0,1-.146.354L6.707,8l2.647,2.646a.5.5,0,0,1-.708.708l-3-3a.5.5,0,0,1,0-.708l3-3A.5.5,0,0,1,9.5,5Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-right">
            <path
              d="M10.5,8a.5.5,0,0,1-.146.354l-3,3a.5.5,0,0,1-.708-.708L9.293,8,6.646,5.354a.5.5,0,0,1,.708-.708l3,3A.5.5,0,0,1,10.5,8Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-dir">
            <path
              d="M14,4H8.414a1,1,0,0,1-.707-.293L6.293,2.293A1,1,0,0,0,5.586,2H2A1,1,0,0,0,1,3V13a1,1,0,0,0,1,1H14a1,1,0,0,0,1-1V5A1,1,0,0,0,14,4ZM2,3H5.586l1,1H2ZM14,13H2V5H14Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-tree">
            <path
              d="M14,5V2H11V3H4V2H1V5H4V4H7V14h4v1h3V12H11v1H8V9h3v1h3V7H11V8H8V4h3V5ZM3,4H2V3H3Zm9,9h1v1H12Zm0-5h1V9H12Zm0-5h1V4H12Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-tree-on">
            <path
              d="M14,5V2H11V3H4V2H1V5H4V4H7V14h4v1h3V12H11v1H8V9h3v1h3V7H11V8H8V4h3V5Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-dir-on">
            <path
              d="M14.878,7H14V5a1,1,0,0,0-1-1H7.414a1,1,0,0,1-.707-.293L5.293,2.293A1,1,0,0,0,4.586,2H1A1,1,0,0,0,0,3V13a1,1,0,0,0,1,1H13.271a1,1,0,0,0,.952-.694l1.607-5A1,1,0,0,0,14.878,7ZM4.586,3,6,4.414A1.986,1.986,0,0,0,7.414,5H13V7H2.979a1,1,0,0,0-.952.694L1,10.889V3Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-db-on">
            <path
              d="M8,1C7,1,2,1.121,2,3.5v9C2,14.879,7,15,8,15s6-.121,6-2.5v-9C14,1.121,9,1,8,1Zm5,10.482c0,.828-2.239,1.5-5,1.5s-5-.672-5-1.5V11c1.511.915,4.281.982,5,.982s3.489-.067,5-.982ZM13,8.5c0,.828-2.239,1.5-5,1.5S3,9.328,3,8.5V8.018C4.511,8.933,7.281,9,8,9s3.489-.067,5-.982Zm0-3C13,6.328,10.761,7,8,7S3,6.328,3,5.5V5.018C4.511,5.933,7.281,6,8,6s3.489-.067,5-.982Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-db">
            <path
              d="M8,1C7,1,2,1.121,2,3.5v9C2,14.879,7,15,8,15s6-.121,6-2.5v-9C14,1.121,9,1,8,1ZM3,8.018C4.511,8.933,7.281,9,8,9s3.489-.067,5-.982V9.5c0,.828-2.239,1.5-5,1.5s-5-.672-5-1.5ZM8,8C5.239,8,3,7.328,3,6.5V5.018C4.511,5.933,7.281,6,8,6s3.489-.067,5-.982V6.5C13,7.328,10.761,8,8,8ZM8,2c2.761,0,5,.672,5,1.5S10.761,5,8,5,3,4.328,3,3.5,5.239,2,8,2ZM8,14c-2.761,0-5-.672-5-1.5V11.018C4.511,11.933,7.281,12,8,12s3.489-.067,5-.982V12.5C13,13.328,10.761,14,8,14Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-close">
            <path
              d="M12.354,11.646a.5.5,0,0,1-.708.708L8,8.707,4.354,12.354a.5.5,0,0,1-.708-.708L7.293,8,3.646,4.354a.5.5,0,0,1,.708-.708L8,7.293l3.646-3.647a.5.5,0,0,1,.708.708L8.707,8Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-search">
            <path
              d="M6.5,1A5.5,5.5,0,1,0,12,6.5,5.5,5.5,0,0,0,6.5,1Zm0,10A4.5,4.5,0,1,1,11,6.5,4.505,4.505,0,0,1,6.5,11Zm8.854,4.354a.5.5,0,0,1-.708,0l-3-3a.5.5,0,0,1,.708-.708l3,3A.5.5,0,0,1,15.354,15.354Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-more">
            <path
              d="M8,6a2,2,0,1,0,2,2A2,2,0,0,0,8,6ZM8,9A1,1,0,1,1,9,8,1,1,0,0,1,8,9ZM2,6A2,2,0,1,0,4,8,2,2,0,0,0,2,6ZM2,9A1,1,0,1,1,3,8,1,1,0,0,1,2,9ZM14,6a2,2,0,1,0,2,2A2,2,0,0,0,14,6Zm0,3a1,1,0,1,1,1-1A1,1,0,0,1,14,9Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-flow">
            <path
              d="M4,3V2H1V5H4V4H7v9a1,1,0,0,0,1,1h5V13H8V9h5V8H8V4h5V3H4ZM3,4H2V3H3Z"
              transform="translate(0 0)"
            />
            <polygon points="13 1.5 13 5.5 15 3.5 13 1.5" />
            <polygon points="13 6.5 13 10.5 15 8.5 13 6.5" />
            <polygon points="13 11.5 13 15.5 15 13.5 13 11.5" />
          </g>
          <g id="icn-expand">
            <path
              d="M6,2.5a.5.5,0,0,1-.5.5H3V5.5a.5.5,0,0,1-1,0v-3A.5.5,0,0,1,2.5,2h3A.5.5,0,0,1,6,2.5ZM13.5,2h-3a.5.5,0,0,0,0,1H13V5.5a.5.5,0,0,0,1,0v-3A.5.5,0,0,0,13.5,2Zm0,8a.5.5,0,0,0-.5.5V13H10.5a.5.5,0,0,0,0,1h3a.5.5,0,0,0,.5-.5v-3A.5.5,0,0,0,13.5,10Zm-8,3H3V10.5a.5.5,0,0,0-1,0v3a.5.5,0,0,0,.5.5h3a.5.5,0,0,0,0-1Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-contract">
            <path
              d="M6,2.5v3a.5.5,0,0,1-.5.5h-3a.5.5,0,0,1,0-1H5V2.5a.5.5,0,0,1,1,0ZM10.5,6h3a.5.5,0,0,0,0-1H11V2.5a.5.5,0,0,0-1,0v3A.5.5,0,0,0,10.5,6Zm3,4h-3a.5.5,0,0,0-.5.5v3a.5.5,0,0,0,1,0V11h2.5a.5.5,0,0,0,0-1Zm-8,0h-3a.5.5,0,0,0,0,1H5v2.5a.5.5,0,0,0,1,0v-3A.5.5,0,0,0,5.5,10Z"
              transform="translate(0 0)"
            />
          </g>
          <g id="icn-filter">
            <path
              d="M14.5,4.5A.5.5,0,0,1,14,5H2A.5.5,0,0,1,2,4H14A.5.5,0,0,1,14.5,4.5ZM12,8H4A.5.5,0,0,0,4,9h8a.5.5,0,0,0,0-1Zm-2,4H6a.5.5,0,0,0,0,1h4a.5.5,0,0,0,0-1Z"
              transform="translate(0 0)"
            />
          </g>
        </svg>
      </div>
      {/* end - icons for dbt docs render */}

      <div ref={angularRef} className={classes.dbtDocs}>
        {/* @ts-expect-error valid tag */}
        <ui-view />
      </div>
    </>
  );
};

export default DbtDocsRenderer;
